/**
 * Responsibility: Implement combat check behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import { CombatStatus } from "@anyquest/shared-core"
import type { SupervisorCheckFn, SupervisorIncidentSeed } from "@anyquest/shared-core"

export const combatCheck: SupervisorCheckFn = (state) => {
  const incidents: SupervisorIncidentSeed[] = []
  const combat = state.combat
  if (!combat) return { passed: true, incidents }

  if (combat.status === CombatStatus.active) {
    const npcName = String(combat.npc?.name ?? '').trim()
    if (!npcName) {
      incidents.push({
        code: 'combat_npc_name_missing',
        domain: 'combat',
        severity: 'high',
        confidence: 0.99,
        message: 'Active combat has no opponent name.',
        remediationPlan: 'Terminate broken combat state safely.',
      })
    }

    const roomNpcExists = (state.currentRoom.npcs ?? []).some(n => n.name === npcName)
    if (!roomNpcExists && npcName) {
      incidents.push({
        code: 'combat_opponent_missing_from_room',
        domain: 'combat',
        severity: 'medium',
        confidence: 0.89,
        message: `Combat opponent (${npcName}) is missing from current room roster.`,
        remediationPlan: 'Re-sync opponent from world NPCs or terminate stale combat.',
        context: { npcName },
      })
    }

    if (!Number.isFinite(combat.round) || combat.round < 1) {
      incidents.push({
        code: 'combat_round_invalid',
        domain: 'combat',
        severity: 'low',
        confidence: 0.97,
        message: `Combat round value is invalid (${String(combat.round)}).`,
        remediationPlan: 'Clamp combat round to at least 1.',
        context: { round: combat.round },
      })
    }
  }

  return { passed: incidents.length === 0, incidents }
}
